import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ArrowRightIcon, PlayCircleIcon, TrophyIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

const Sports = ({ onLoadingChange }) => {
  const navigate = useNavigate();
  const [leagueData, setLeagueData] = useState([]);
  const [pastMatches, setPastMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [players, setPlayers] = useState({});

  useEffect(() => {
    let mounted = true;
    onLoadingChange?.(true);
    fetchAllData().finally(() => {
      if (mounted) onLoadingChange?.(false);
    });
    return () => { mounted = false; };
    // Fetch once when the home page mounts; existing match caches are component-local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScores = async (matchId) => {
    if (scores[matchId]) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/scores/matches/${matchId}/scores`
      );
      setScores((prev) => ({ ...prev, [matchId]: response.data }));
    } catch (error) {
      console.error("Error fetching scores:", error);
      toast.error("Failed to fetch scores.");
    }
  };

  const fetchPlayerName = async (playerId) => {
    if (players[playerId]) return players[playerId];

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/players/${playerId}`
      );
      const playerName = response.data.name;
      setPlayers((prev) => ({ ...prev, [playerId]: playerName }));
      return playerName;
    } catch (error) {
      console.error("Error fetching player name:", error);
      return "Unknown Player";
    }
  };

  const fetchAllData = async () => {
    try {
      const [matchesResponse, teamsResponse, leaguesResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/matches`),
        fetch(`${process.env.REACT_APP_API_URL}/clubs`),
        fetch(`${process.env.REACT_APP_API_URL}/leagues`),
      ]);

      if (!matchesResponse.ok || !teamsResponse.ok || !leaguesResponse.ok) {
        throw new Error("Failed to fetch data");
      }

      const [matches, teams, leagues] = await Promise.all([
        matchesResponse.json(), teamsResponse.json(), leaguesResponse.json(),
      ]);
      const leaguesArray = Array.isArray(leagues) ? leagues : leagues.data || [];
      const teamsArray = Array.isArray(teams) ? teams : teams.data || [];
      const matchesArray = Array.isArray(matches) ? matches : matches.data || [];

      const teamsLookup = teamsArray.reduce((acc, team) => {
        acc[team.clubId] = { name: team.name, logo: team.logo };
        return acc;
      }, {});
      const leaguesLookup = leaguesArray.reduce((acc, league) => {
        acc[league.leagueId] = league.name;
        return acc;
      }, {});

      const now = new Date();
      const upcomingMatches = [];
      const completedMatches = [];

      matchesArray.forEach((match) => {
        const matchDate = new Date(match.date);
        const matchData = {
          id: match.matchesId,
          time: matchDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }),
          date: matchDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          leagueName: leaguesLookup[match.leagueId] || "Unknown League",
          homeTeam: teamsLookup[match.homeTeamId],
          awayTeam: teamsLookup[match.awayTeamId],
          homeTeamId: match.homeTeamId,
          awayTeamId: match.awayTeamId,
          homeTeamScore: match.homeTeamScore,
          awayTeamScore: match.awayTeamScore,
          leagueId: match.leagueId,
          matchesId: match.matchesId,
        };

        if (matchDate <= now) completedMatches.push(matchData);
        else upcomingMatches.push(matchData);
      });

      const groupedMatches = upcomingMatches.reduce((acc, match) => {
        if (!acc[match.leagueId]) {
          acc[match.leagueId] = { id: match.leagueId, name: match.leagueName, matches: [] };
        }
        if (match.homeTeam && match.awayTeam) {
          acc[match.leagueId].matches.push({
            id: match.id,
            time: match.time,
            date: match.date,
            teams: `${match.homeTeam.name} v ${match.awayTeam.name}`,
          });
        }
        return acc;
      }, {});

      const leaguesWithMatches = Object.values(groupedMatches)
        .filter((league) => league.matches.length > 0)
        .map((league) => ({
          ...league,
          matches: league.matches.sort((a, b) => new Date(a.date) - new Date(b.date)),
        }));
      const recentMatches = completedMatches
        .filter((match) => match.homeTeam && match.awayTeam)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);

      await Promise.all(recentMatches.map((match) => fetchScores(match.matchesId)));
      setLeagueData(leaguesWithMatches);
      setPastMatches(recentMatches);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchMatchDetails = async (match) => {
    setSelectedMatch(match);
    if (!scores[match.matchesId]) await fetchScores(match.matchesId);
    if (scores[match.matchesId]) {
      await Promise.all(
        scores[match.matchesId].map((score) => fetchPlayerName(score.playerId))
      );
    }
  };

  if (loading) {
    return (
      <section aria-label="Sports and fixtures" className="bg-surface-950 px-5 py-8 sm:px-8 lg:px-10">
        <div role="status" className="mx-auto flex min-h-40 max-w-7xl items-center justify-center rounded-xl border border-line bg-surface-900 text-sm text-gray-400">
          Loading fixtures…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-label="Sports and fixtures" className="bg-surface-950 px-5 py-8 sm:px-8 lg:px-10">
        <div role="alert" className="mx-auto max-w-7xl rounded-xl border border-line bg-surface-900 p-6 text-sm text-gray-300">
          Unable to load fixtures: {error}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Sports and fixtures" className="bg-surface-950 py-8 text-white sm:py-10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <p className="eyebrow-accent">FOLLOW THE ACTION</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">Sports & fixtures</h2>
            <p className="mt-1 text-sm text-gray-400">Upcoming matches and recent results</p>
          </div>
          <button type="button" onClick={() => navigate("/sports")} className="hidden shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-surface-800 hover:text-white sm:inline-flex">
            Sports hub <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        {leagueData.length === 0 && pastMatches.length === 0 && (
          <p className="rounded-xl border border-line bg-surface-900 px-5 py-8 text-center text-sm text-gray-400">
            No fixtures or recent results are available right now.
          </p>
        )}

        {leagueData.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {leagueData.map((league) => (
              <article key={league.id} className="overflow-hidden rounded-xl border border-line bg-surface-850 transition-colors duration-200 hover:border-line-strong">
                <div className="flex items-center gap-2 border-b border-line bg-surface-900 px-5 py-4">
                  <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                  <h3 className="text-base font-semibold text-white">{league.name}</h3>
                </div>
                <div className="p-3 sm:p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed">
                      <thead>
                        <tr className="border-b border-line">
                          <th scope="col" className="w-24 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Date/Time</th>
                          <th scope="col" className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">Teams</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {league.matches.map((match) => (
                          <tr key={match.id} className="transition-colors duration-150 hover:bg-surface-800">
                            <td className="whitespace-nowrap px-3 py-3">
                              <span className="block text-sm font-medium text-gray-200">{match.date}</span>
                              <span className="text-xs text-gray-500">{match.time}</span>
                            </td>
                            <td className="break-words px-3 py-3 text-sm font-medium text-gray-200">{match.teams}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mb-5 mt-10 flex items-end justify-between gap-3">
          <div>
            <p className="eyebrow-accent">MATCHDAY RECAPS</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">Recent highlights</h2>
          </div>
          <button type="button" onClick={() => navigate("/highlights")} className="hidden shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-surface-800 hover:text-white sm:inline-flex">
            Watch more <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>

        {pastMatches.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {pastMatches.map((match) => {
              const matchScores = scores[match.matchesId] || [];
              const homeGoals = match.homeTeamScore ?? matchScores.filter((score) => score.clubId === match.homeTeamId).length;
              const awayGoals = match.awayTeamScore ?? matchScores.filter((score) => score.clubId === match.awayTeamId).length;

              return (
                <article key={match.id} className="overflow-hidden rounded-xl border border-line bg-surface-850 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong">
                  <div className="flex items-center justify-between border-b border-line bg-surface-900 px-5 py-3">
                    <span className="text-sm font-semibold text-gray-200">{match.leagueName}</span>
                    <span className="text-xs text-gray-400">{match.date}</span>
                  </div>
                  <div className="space-y-5 p-5 sm:p-6">
                    <TeamScore team={match.homeTeam} score={homeGoals} />
                    <div className="flex items-center gap-3" aria-label="Full time">
                      <span className="h-px flex-1 bg-line" />
                      <span className="text-xs font-bold tracking-wider text-accent-soft">FT</span>
                      <span className="h-px flex-1 bg-line" />
                    </div>
                    <TeamScore team={match.awayTeam} score={awayGoals} />
                    <button type="button" onClick={() => fetchMatchDetails(match)} className="btn-secondary min-h-11 w-full gap-2">
                      <PlayCircleIcon aria-hidden="true" className="h-4 w-4" />
                      Match details
                      <ArrowRightIcon aria-hidden="true" className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <p className="rounded-xl border border-line bg-surface-900 px-5 py-6 text-center text-sm text-gray-400">Recent match highlights will appear here.</p>
        )}

        {selectedMatch && (
          <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" role="presentation" onClick={() => setSelectedMatch(null)}>
            <div role="dialog" aria-modal="true" aria-labelledby="match-details-title" className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-line-strong bg-surface-850 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-line bg-surface-900 p-4 sm:p-6">
                <h3 id="match-details-title" className="text-xl font-bold text-white sm:text-2xl">{selectedMatch.leagueName}</h3>
                <button type="button" aria-label="Close match details" onClick={() => setSelectedMatch(null)} className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-surface-700 hover:text-white">✕</button>
              </div>
              <div className="p-5 sm:p-6">
                <div className="mb-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="flex items-center gap-3"><img src={selectedMatch.homeTeam.logo} alt="" className="h-10 w-10 object-contain sm:h-12 sm:w-12" /><span className="text-base text-gray-200 sm:text-lg">{selectedMatch.homeTeam.name}</span></div>
                  <div className="text-xl font-bold tabular-nums text-white sm:text-2xl">
                    {selectedMatch.homeTeamScore != null && selectedMatch.awayTeamScore != null
                      ? `${selectedMatch.homeTeamScore} - ${selectedMatch.awayTeamScore}`
                      : `${(scores[selectedMatch.matchesId] || []).filter((score) => score.clubId === selectedMatch.homeTeamId).length} - ${(scores[selectedMatch.matchesId] || []).filter((score) => score.clubId === selectedMatch.awayTeamId).length}`}
                  </div>
                  <div className="flex items-center gap-3"><span className="text-base text-gray-200 sm:text-lg">{selectedMatch.awayTeam.name}</span><img src={selectedMatch.awayTeam.logo} alt="" className="h-10 w-10 object-contain sm:h-12 sm:w-12" /></div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[selectedMatch.homeTeamId, selectedMatch.awayTeamId].map((clubId, index) => (
                    <div key={clubId} className={`space-y-2 rounded-lg border border-line bg-surface-900 p-3 ${index === 1 ? "sm:text-right" : ""}`}>
                      {(scores[selectedMatch.matchesId] || [])
                        .filter((score) => score.clubId === clubId)
                        .sort((a, b) => a.timeScored - b.timeScored)
                        .map((score) => (
                          <div key={score.id} className="flex items-center gap-2 py-2 text-sm">
                            {score.timeScored != null && <span className="text-gray-500">{score.timeScored}'</span>}
                            <span className="font-medium text-gray-200">{score.playerId ? players[score.playerId] || "Loading…" : "Unattributed goal"}</span>
                            <span className="text-gray-500">{score.isPenalty && "(P)"}{score.isOwnGoal && "(OG)"}</span>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

function TeamScore({ team, score }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <div className="h-12 w-12 shrink-0 sm:h-14 sm:w-14">
        <img src={team.logo} alt="" className="h-full w-full rounded-full border border-line bg-surface-900 object-contain p-2" />
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-200 sm:text-base">{team.name}</span>
      <span className="text-xl font-bold tabular-nums text-white sm:text-2xl">{score}</span>
    </div>
  );
}

export default Sports;
